import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useDialog } from '../context/DialogContext';
import LoadingSpinner from '../components/LoadingSpinner';
import GiveTakeBoard from '../components/GiveTakeBoard';
import AddPeopleModal from '../components/AddPeopleModal';
import GatheringCheckoutForm from '../components/GatheringCheckoutForm';
import GatheringEntryForm from '../components/GatheringEntryForm';
import gatheringService from '../services/gatheringService';
import clubService from '../services/clubService';
import useIsMobile from '../hooks/useIsMobile';
import useContainerWidth from '../hooks/useContainerWidth';
import ProCoverEditor from '../components/pro/ProCoverEditor';
import GatheringCover from '../components/gathering/GatheringCover';
import useGatheringCover from '../components/gathering/useGatheringCover';
import { GATHERING_COVER_COPY } from '../components/gathering/gatheringCoverModel';
import { SurfacePage, SoftSurface, StickyRail, StickyCtaBar, EmptyBox, RING_ON } from '../components/surface';
import { PillButton, RingTab, TagPill } from '../components/club/clubUi';
import { Sheet, Segmented, OverlapChip, RingCta, Bar, FacePill, RoundFace, kicker, STARTER, STARTER_TRACK, CURATED, CYAN_TINT } from '../components/gathering/gatheringUi';
import {
  dayKey, dayShort, rangeDays, wholeLabel, isChargeable, isTicketed, priceLabel, groupByDay, takeAllLabels, agendaSubLabel, hostFill, availableTabs, gatheringStatus, STATUS_PILL,
} from '../components/gathering/gatheringModel';
import { computeOverlaps } from '../utils/agendaOverlap';
import { money, dayRangeLabel, timeRangeLabel, dateLabel, dayLabel, plural, crumb } from '../utils/surfaceFormat';

export { wholeLabel };

/**
 * /clubs/:id/gatherings/:gatheringId + /gatherings/:gatheringId — design_handoff_gatherings §1
 * "Gathering page" (Oct 2026). A faded cover, the day strip, the host / you're-in band, the
 * entry panel, a day-grouped agenda with per-row actions, a 380px rail (all-in · your weekend ·
 * the numbers · who's in) and one tab panel at a time under the agenda. On the phone the rail
 * sits inline and a black pill bar carries the CTA.
 *
 * Money truth: there is NO bundle price and NO discount. "Take the weekend" charges each ticketed
 * event separately (card-only, `GatheringCheckoutForm`), so the card shows the plain sum of the
 * tickets plus what the fees add on top — never a fake saving.
 */

const MUTED = '#6B7280';
const SUBTLE = '#9CA3AF';
const BODY = '#374151';
const GREEN = '#16A34A';
const GREEN_PILL = '#22C55E';
const CHIP = '#F2F2F2';
const RULE = '2px solid #F2F2F2';

const ENTRY_CHIP = {
  pending: { text: 'APPLIED · WAITING ON THE HOST', bg: '#FACC15', fg: '#000' },
  declined: { text: 'NOT ACCEPTED', bg: CHIP, fg: MUTED },
  waitlisted: { text: 'WAITLISTED', bg: CHIP, fg: MUTED },
  refund_pending: { text: 'REFUND ON ITS WAY', bg: '#FEE2E2', fg: '#B91C1C' },
  refund_failed: { text: 'REFUND NEEDS A LOOK', bg: '#FEE2E2', fg: '#B91C1C' },
};

/**
 * Statuses that are OVER. The entry they describe is finished, so the gathering is open to this
 * person again — a refunded guest who still wants to come must be able to say so, and before this
 * they saw neither a button nor a word.
 */
const SPENT_ENTRY = ['refunded', 'declined', 'withdrawn'];

const err = (r) => r?.error?.message || r?.error || 'Something went wrong.';

export default function GatheringPage() {
  const navigate = useNavigate();
  const { id: routeClubId, gatheringId } = useParams();
  const { profile } = useUser();
  const { showConfirm, showAlert } = useDialog();
  const mobile = useIsMobile();
  const mainRef = useRef(null);
  const width = useContainerWidth(mainRef);
  const wide = !mobile && width >= 1040;

  const [gathering, setGathering] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('full'); // full | mine
  const [dayFilter, setDayFilter] = useState(null); // null | dayKey
  const [tab, setTab] = useState('ann');
  const [announcements, setAnnouncements] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [subtaskDrafts, setSubtaskDrafts] = useState({});
  const [assignForTask, setAssignForTask] = useState(null);
  const [draft, setDraft] = useState('');
  const [pin, setPin] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyResult, setBuyResult] = useState(null);
  const [manage, setManage] = useState(null);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [compOpen, setCompOpen] = useState(false);
  const [coHostOpen, setCoHostOpen] = useState(false);
  const [entryBusy, setEntryBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  // Club gatherings: 'loading' → 'member' | 'none' | 'unknown' (club fetch failed → keep
  // the buttons and let the server error surface). Hosts + standalone are always 'member'.
  const [membership, setMembership] = useState('loading');
  // The cover document (photos · layout · split · count · card) + EDIT COVER for the host.
  const [coverEditOpen, setCoverEditOpen] = useState(false);
  const { shapedCover, setCover, coverPhotos, layoutKey, onFocal, api: coverApi } = useGatheringCover(gathering);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await gatheringService.get(gatheringId);
    if (res.success) setGathering(res.data);
    setLoading(false);
  }, [gatheringId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    gatheringService.getAnnouncements(gatheringId).then((r) => { if (r.success) setAnnouncements(r.data || []); });
  }, [gatheringId]);

  const loadTasks = useCallback(async () => {
    const r = await gatheringService.getTasks(gatheringId);
    if (r.success) setTasks(r.data || []);
  }, [gatheringId]);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  const loadManage = useCallback(async () => {
    const r = await gatheringService.getManage(gatheringId);
    if (r.success) setManage(r.data);
  }, [gatheringId]);
  useEffect(() => { if (gathering?.isHost) loadManage(); }, [gathering?.isHost, loadManage]);

  // Who can RSVP / buy in: the server decides (CanParticipateAsync) but the DTO carries no
  // flag, so a club gathering asks the club for the viewer's role and renders read-only for
  // non-members instead of showing buttons that fail.
  const gClubId = gathering?.clubId;
  const gIsHost = gathering?.isHost;
  const gStandalone = gathering?.isStandalone || !gathering?.clubId;
  useEffect(() => {
    if (!gathering) return undefined;
    if (gIsHost || gStandalone) { setMembership('member'); return undefined; }
    let on = true;
    setMembership('loading');
    clubService.getClub(gClubId).then((r) => {
      if (!on) return;
      if (!r.success) { setMembership('unknown'); return; }
      setMembership(r.data?.userRole ? 'member' : 'none');
    });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gClubId, gIsHost, gStandalone, !!gathering]);

  const canParticipate = membership === 'member' || membership === 'unknown';
  const participationKnown = membership !== 'loading';

  const allAgenda = useMemo(() => gathering?.agenda || [], [gathering]);
  const chargeable = useMemo(() => allAgenda.filter(isChargeable), [allAgenda]);
  const chargeableKey = chargeable.map((e) => e.id).join(',');
  const overlapMap = useMemo(() => computeOverlaps(allAgenda), [allAgenda]);

  // The all-in quote drives the CTA price. Loaded only for participants with something to buy.
  useEffect(() => {
    if (!gathering || gathering.isHost || !canParticipate || !chargeableKey) { setQuote(null); return undefined; }
    let on = true;
    gatheringService.checkoutAllQuote(gatheringId).then((r) => { if (on && r.success) setQuote(r.data); });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatheringId, gathering?.isHost, canParticipate, chargeableKey]);

  const setRsvp = async (eventId, status) => {
    setGathering((g) => ({
      ...g,
      agenda: g.agenda.map((e) => (e.id === eventId ? { ...e, viewerRsvp: e.viewerRsvp === status ? null : status } : e)),
    }));
    const r = await gatheringService.setRsvp(eventId, status);
    if (!r.success) {
      await showAlert({ title: 'COULD NOT RSVP', message: err(r), type: 'error' });
      load();
    }
  };

  const openBuy = () => { setBuyResult(null); setBuyOpen(true); };

  const goingToAll = async () => {
    setBulkBusy(true);
    const r = await gatheringService.rsvpAll(gatheringId, 'going');
    setBulkBusy(false);
    if (!r.success) { await showAlert({ title: 'COULD NOT UPDATE', message: err(r), type: 'error' }); return; }
    load();
    if (chargeable.length > 0) openBuy();
  };

  const onBought = (lines) => {
    setBuyResult(lines || []);
    load();
    if (gathering?.isHost) loadManage();
  };

  // Apply to the gathering entry. Paid → create the app then open the pay sheet;
  // free/curated → just create the (pending/accepted) application.
  const startApply = async () => {
    setEntryBusy(true);
    const r = await gatheringService.apply(gatheringId);
    setEntryBusy(false);
    if (!r.success) { await showAlert({ title: 'COULD NOT APPLY', message: err(r), type: 'error' }); return; }
    if ((gathering?.entryFeeCents || 0) > 0 && (r.data?.status === 'pending_payment' || r.data?.status === 'accepted')) {
      setEntryOpen(true);
    } else {
      load();
    }
  };
  const onEntryPaid = () => { setEntryOpen(false); load(); if (gathering?.isHost) loadManage(); };

  const reviewApplicant = async (userId, action) => {
    const r = await gatheringService.reviewApplicant(gatheringId, userId, action);
    if (!r.success) { await showAlert({ title: 'COULD NOT UPDATE', message: err(r), type: 'error' }); return; }
    load();
  };
  const compGuests = async (userIds) => {
    setCompOpen(false);
    const results = await Promise.all((userIds || []).map((id) => gatheringService.comp(gatheringId, id)));
    const failed = results.find((x) => !x.success);
    if (failed) { await showAlert({ title: 'COULD NOT COMP', message: err(failed), type: 'error' }); }
    load();
  };
  const greenlightGathering = async () => {
    const ok = await showConfirm({ title: 'GREENLIGHT THIS GATHERING?', message: 'This charges the accepted guests and locks it in.', confirmLabel: 'GREENLIGHT', cancelLabel: 'NOT YET' });
    if (!ok) return;
    const r = await gatheringService.greenlight(gatheringId);
    if (!r.success) { await showAlert({ title: 'COULD NOT GREENLIGHT', message: err(r), type: 'error' }); return; }
    load();
  };

  const invitePeople = async (userIds) => {
    const results = await Promise.all(userIds.map((id) => gatheringService.invite(gatheringId, id)));
    setInviteOpen(false);
    const failed = results.find((r) => !r.success);
    if (failed) { await showAlert({ title: 'COULD NOT INVITE', message: err(failed), type: 'error' }); return; }
    await showAlert({ title: 'INVITES SENT', message: `Invited ${userIds.length} ${userIds.length === 1 ? 'person' : 'people'} to ${gathering?.name}.`, type: 'success' });
  };

  const addCoHosts = async (userIds) => {
    setCoHostOpen(false);
    const results = await Promise.all((userIds || []).map((id) => gatheringService.addCoHost(gatheringId, id)));
    const failed = results.find((x) => !x.success);
    if (failed) { await showAlert({ title: 'COULD NOT ADD CO-HOST', message: err(failed), type: 'error' }); }
    load();
  };
  const removeCoHost = async (m) => {
    const self = m.userId === profile?.userId;
    const ok = await showConfirm({
      title: self ? 'STEP DOWN AS CO-HOST?' : `REMOVE ${(m.displayName || 'THIS CO-HOST').toUpperCase()}?`,
      message: self ? 'You stop running this gathering. The owner can add you back.' : 'They stop running this gathering. Nothing they made is removed.',
      confirmLabel: self ? 'STEP DOWN' : 'REMOVE', cancelLabel: 'KEEP', variant: 'danger',
    });
    if (!ok) return;
    const r = await gatheringService.removeCoHost(gatheringId, m.userId);
    if (!r.success) { await showAlert({ title: 'COULD NOT REMOVE', message: err(r), type: 'error' }); return; }
    if (self) navigate(`/gatherings/${gatheringId}`, { replace: true });
    load();
  };

  const backTo = () => navigate(routeClubId || gathering?.clubId ? `/clubs/${routeClubId || gathering.clubId}` : '/host');

  const deleteGathering = async () => {
    const ok = await showConfirm({
      title: 'DELETE THIS GATHERING?',
      message: `"${gathering?.name}" will be removed. Its events are kept — they just become standalone events again.`,
      confirmLabel: 'DELETE',
      cancelLabel: 'KEEP',
      variant: 'danger',
    });
    if (!ok) return;
    const r = await gatheringService.delete(gatheringId);
    if (!r.success) { await showAlert({ title: 'COULD NOT DELETE', message: err(r), type: 'error' }); return; }
    backTo();
  };

  const postAnnouncement = async () => {
    const content = draft.trim();
    if (!content) return;
    const r = await gatheringService.createAnnouncement(gatheringId, { content, isPinned: pin });
    if (r.success) {
      setAnnouncements((prev) => [r.data, ...prev.filter((a) => a.id !== r.data.id)]);
      setDraft('');
      setPin(false);
    } else {
      await showAlert({ title: 'COULD NOT POST', message: err(r), type: 'error' });
    }
  };
  const deleteAnnouncement = async (id) => {
    const ok = await showConfirm({ title: 'DELETE THIS ANNOUNCEMENT?', message: 'It comes off the board for everyone.', confirmLabel: 'DELETE', cancelLabel: 'KEEP', variant: 'danger' });
    if (!ok) return;
    const r = await gatheringService.deleteAnnouncement(gatheringId, id);
    if (r.success) setAnnouncements((prev) => prev.filter((a) => a.id !== id));
  };

  // ── Tasks ──
  const addTask = async (title, parentTaskId = null) => {
    const t = (title || '').trim();
    if (!t) return;
    const r = await gatheringService.createTask(gatheringId, { title: t, parentTaskId });
    if (!r.success) { await showAlert({ title: 'COULD NOT ADD TASK', message: err(r), type: 'error' }); return; }
    if (parentTaskId) setSubtaskDrafts((d) => ({ ...d, [parentTaskId]: '' }));
    else setNewTask('');
    loadTasks();
  };
  const toggleClaim = async (task) => {
    const mine = !task.ownerIsAssigned && task.claimedByUserId === profile?.userId;
    const r = await gatheringService.claimTask(gatheringId, task.id, !mine);
    if (!r.success) { await showAlert({ title: 'COULD NOT UPDATE', message: err(r), type: 'error' }); return; }
    loadTasks();
  };
  const assignTaskTo = async (userIds) => {
    const taskId = assignForTask;
    setAssignForTask(null);
    if (!taskId || !userIds?.length) return;
    const r = await gatheringService.assignTask(gatheringId, taskId, userIds[0]);
    if (!r.success) { await showAlert({ title: 'COULD NOT ASSIGN', message: err(r), type: 'error' }); return; }
    loadTasks();
  };
  const unassignTask = async (taskId) => {
    const r = await gatheringService.assignTask(gatheringId, taskId, null);
    if (r.success) loadTasks();
  };
  const removeTask = async (taskId) => {
    const ok = await showConfirm({ title: 'DELETE THIS TASK?', message: 'It (and any subtasks) will be removed.', confirmLabel: 'DELETE', cancelLabel: 'KEEP', variant: 'danger' });
    if (!ok) return;
    const r = await gatheringService.deleteTask(gatheringId, taskId);
    if (r.success) loadTasks();
  };

  // ── Loading / missing ──
  const frame = (children) => (
    <SoftSurface>
      {mobile
        ? <SurfacePage mobileTitle="GATHERING" mobileEyebrow="GATHERING" onBack={backTo}>{children}</SurfacePage>
        : <SurfacePage maxWidth={1280}>{children}</SurfacePage>}
    </SoftSurface>
  );
  if (loading) return frame(<div className="flex justify-center py-20"><LoadingSpinner /></div>);
  if (!gathering) {
    return frame(
      <div style={{ padding: mobile ? 12 : 0 }}>
        <EmptyBox title="THIS GATHERING ISN'T AVAILABLE." mobile={mobile} action={<PillButton size={40} onClick={backTo}>{routeClubId ? '← BACK TO THE CLUB' : '← HOST DASHBOARD'}</PillButton>}>
          It may be a draft, or it may have been removed.
        </EmptyBox>
      </div>,
    );
  }

  // ── Derived ──
  const g = gathering;
  const clubId = routeClubId || g.clubId;
  const isClub = !!g.clubId && !g.isStandalone;
  const backLabel = isClub ? (g.clubName || 'CLUB') : 'HOST DASHBOARD';
  const editPath = clubId && isClub ? `/clubs/${clubId}/gatherings/${gatheringId}/edit` : `/gatherings/${gatheringId}/edit`;
  // The gathering's own manager — never the pass event's EventOperationPage.
  const managePath = clubId && isClub ? `/clubs/${clubId}/gatherings/${gatheringId}/manage` : `/gatherings/${gatheringId}/manage`;

  const days = rangeDays(g.startsAt, g.endsAt);
  const dayCounts = {};
  allAgenda.forEach((e) => { const k = dayKey(e.dateTime); dayCounts[k] = (dayCounts[k] || 0) + 1; });
  const activeDay = days.find((x) => x.key === dayFilter) || null;

  let agenda = allAgenda;
  if (view === 'mine') agenda = agenda.filter((e) => e.viewerRsvp === 'going' || e.viewerRsvp === 'maybe' || e.viewerHasTicket);
  if (dayFilter) agenda = agenda.filter((e) => dayKey(e.dateTime) === dayFilter);
  const agendaDays = groupByDay(agenda);
  const totalDays = new Set(allAgenda.map((e) => dayKey(e.dateTime))).size || days.length;

  const allGoing = allAgenda.length > 0 && allAgenda.filter((e) => !e.isExternalTicketing).every((e) => e.viewerRsvp === 'going' || e.viewerHasTicket);
  const city = allAgenda.map((e) => e.locationCity).find(Boolean) || null;

  // Bundle figures: the quote when it's in (it also drops curated events), else the agenda.
  const bundleLines = quote?.events?.length ? quote.events.map((l) => ({ id: l.eventId, title: l.title, cents: l.ticketCents })) : chargeable.map((e) => ({ id: e.id, title: e.title, cents: e.ticketPriceCents }));
  const bundleCount = bundleLines.length;
  const bundleSum = quote?.events?.length ? quote.ticketCents : chargeable.reduce((s, e) => s + e.ticketPriceCents, 0);
  const bundleTotal = quote?.events?.length ? quote.grandTotalCents : null;
  const showBundle = !g.isHost && canParticipate && bundleCount > 0;
  const labels = takeAllLabels(bundleCount, bundleSum, g.startsAt, g.endsAt);
  const notInCharge = allAgenda.filter((e) => !isChargeable(e) && !e.viewerHasTicket);

  const heldCount = allAgenda.filter((e) => e.viewerHasTicket).length;
  const goingCount = allAgenda.filter((e) => !e.viewerHasTicket && e.viewerRsvp === 'going').length;
  const allIn = !g.isHost && canParticipate && chargeable.length === 0 && allAgenda.some((e) => e.viewerHasTicket || e.viewerRsvp === 'going');

  const showEntry = !g.isHost && g.requiresApplication && canParticipate;
  const showMembersOnly = participationKnown && !canParticipate;
  const fee = g.entryFeeCents > 0;
  const starter = g.fundingType === 'party_starter';
  const entryStatus = g.viewerApplicationStatus;

  const people = [];
  const seen = new Set();
  [...(g.hosts || []), ...(g.members || [])].forEach((m) => { if (!seen.has(m.userId)) { seen.add(m.userId); people.push(m); } });
  const whoCount = Math.max(g.memberCount || 0, people.length);

  const tabs = availableTabs({ isHost: g.isHost, requiresApplication: g.requiresApplication, giveTakeEnabled: g.giveTakeEnabled });
  const activeTab = tabs.some((t) => t.key === tab) ? tab : 'ann';

  const status = gatheringStatus(g);
  const statusPill = STATUS_PILL[status] || STATUS_PILL.draft;
  const accessLine = [g.mode === 'curated' ? (isClub ? 'MEMBERS · HOST APPROVES' : 'HOST APPROVES') : (isClub ? 'OPEN TO MEMBERS' : 'OPEN'), g.isPrivate ? 'PRIVATE' : null].filter(Boolean).join(' · ');
  const mobileEyebrow = ['GATHERING', g.isPrivate ? 'PRIVATE' : 'OPEN'].join(' · ');
  const panelPad = mobile ? '18px 16px' : '22px 24px';
  const gap = mobile ? 10 : 16;
  const openProfile = (m) => m.handle && navigate(`/${m.handle}`);

  // ── Cover ──
  const byLine = isClub
    ? { src: g.clubAvatarUrl, name: g.clubName, label: 'A GATHERING BY', onClick: () => navigate(`/clubs/${clubId}`) }
    : g.hosts?.[0] ? { src: g.hosts[0].avatarUrl, name: g.hosts[0].displayName, label: 'HOSTED BY', onClick: () => openProfile(g.hosts[0]) } : null;
  const hasCoverPhotos = coverPhotos.length > 0;
  const identity = (
    <div className="relative flex flex-col" style={{ gap: mobile ? 12 : 16, maxWidth: !mobile && !hasCoverPhotos ? '60%' : undefined }} data-testid={mobile ? 'mobile-header' : 'cover-identity'}>
      <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
        <TagPill bg="#00FFFF" size={10} padding="5px 12px">GATHERING</TagPill>
        <TagPill bg={statusPill.bg} fg={statusPill.fg} ring={statusPill.ring} size={10} padding="5px 12px">{statusPill.label}</TagPill>
        <span style={{ ...kicker(10), color: MUTED }} data-testid="access-line">{accessLine}</span>
      </span>
      <h1 style={{ margin: 0, textTransform: 'none', fontSize: mobile ? 28 : width > 1100 ? 48 : 40, fontWeight: 700, lineHeight: 1.08, letterSpacing: '-.01em', overflowWrap: 'anywhere' }}>{g.name}</h1>
      {g.description && <p style={{ margin: 0, maxWidth: 520, fontSize: mobile ? 12 : 14, lineHeight: 1.65, color: BODY }}>{g.description}</p>}
      <span className="flex flex-wrap items-baseline" style={{ gap: '6px 16px' }}>
        <span style={{ fontSize: mobile ? 13 : 16, fontWeight: 700, letterSpacing: '.04em' }}>{dayRangeLabel(g.startsAt, g.endsAt)}</span>
        {city && <span style={{ fontSize: 12, color: MUTED }}>{city}</span>}
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', color: GREEN }} data-testid="who-count">{whoCount} IN</span>
      </span>
      {byLine && (
        <button type="button" onClick={byLine.onClick} className="flex items-center text-left" style={{ gap: 10, marginTop: 4, border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer', pointerEvents: 'auto' }} data-testid="by-line">
          <RoundFace src={byLine.src} name={byLine.name} size={40} />
          <span className="flex flex-col" style={{ gap: 2 }}>
            <span style={{ ...kicker(9), color: MUTED }}>{byLine.label}</span>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{byLine.name}</span>
          </span>
        </button>
      )}
    </div>
  );
  const coverNode = (
    <GatheringCover gathering={g} identity={identity} photos={coverPhotos} layout={layoutKey} split={shapedCover.split} phone={mobile}
      host={!!g.isHost} editing={!!g.isHost && coverEditOpen} onToggleEdit={() => setCoverEditOpen((o) => !o)} onFocal={onFocal} />
  );
  const cover = mobile ? (
    <section className="flex flex-col bg-white font-mono" style={{ gap: 0 }} data-testid="gathering-cover-frame">
      {coverNode}
      <div style={{ padding: '18px 16px 20px' }}>{identity}</div>
    </section>
  ) : coverNode;
  const coverEditor = g.isHost && coverEditOpen && (
    <ProCoverEditor pageId={g.id} api={coverApi} copy={GATHERING_COVER_COPY} canRemove showVideo={false} testId="gathering-cover-editor" cover={shapedCover} setCover={setCover}
      onReload={() => load()} onClose={() => setCoverEditOpen(false)} phone={mobile} />
  );

  // ── Day strip ──
  const dayStrip = days.length > 1 && (
    <div className="grid" style={{ gridTemplateColumns: `repeat(${Math.min(days.length, 4)},minmax(0,1fr))`, gap: mobile ? 2 : 6 }} data-testid="day-strip">
      {days.map((d) => {
        const on = dayFilter === d.key;
        const n = dayCounts[d.key] || 0;
        return (
          <button key={d.key} type="button" aria-pressed={on} onClick={() => setDayFilter(on ? null : d.key)} className="flex flex-col items-start font-mono"
            style={{ gap: 4, padding: mobile ? '12px 14px' : '14px 18px', border: '2px solid transparent', borderRadius: mobile ? 0 : 20, background: on ? RING_ON : '#fff', color: on ? '#fff' : '#000', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer', transition: 'background-color .2s, color .2s', boxSizing: 'border-box' }} data-testid={`day-cell-${d.key}`}>
            <span style={{ fontSize: mobile ? 12 : 14, fontWeight: 800, letterSpacing: '.06em' }}>{d.short}</span>
            <span style={{ ...kicker(9, '.14em'), opacity: 0.7 }}>{plural(n, 'EVENT')}{on && !mobile ? ' · SHOWING' : ''}</span>
          </button>
        );
      })}
    </div>
  );

  // ── Bands ──
  const toolPill = (label, onClick, { fill = false, color = '#fff', border = '#374151', testId } = {}) => (
    <button key={label} type="button" onClick={onClick} className="font-mono" data-testid={testId}
      style={{ flexShrink: 0, height: 36, padding: '0 14px', border: `2px solid ${fill ? '#fff' : border}`, borderRadius: 999, background: fill ? '#fff' : 'transparent', color: fill ? '#000' : color, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background-color .2s, color .2s' }}>
      {label}
    </button>
  );
  const hostBar = g.isHost && (
    <div className="flex flex-wrap items-center font-mono" style={{ gap: '10px 14px', padding: mobile ? '14px 16px' : '16px 20px', borderRadius: 20, background: '#000', color: '#fff' }} data-testid="host-bar">
      <TagPill bg="#00FFFF" size={9} padding="4px 10px">{g.isOwner ? 'YOU HOST THIS' : 'YOU CO-HOST THIS'}</TagPill>
      <span style={{ fontSize: 11, color: SUBTLE }}>Guests see the agenda and the all-in card. Only the hosts see the tools.</span>
      <span className="flex" style={{ flexWrap: mobile ? 'nowrap' : 'wrap', gap: 6, marginLeft: 'auto', overflowX: 'auto', maxWidth: '100%', scrollbarWidth: 'none' }} data-testid="host-actions">
        {toolPill('EDIT', () => navigate(editPath), { fill: true })}
        {toolPill('MANAGE', () => navigate(managePath), { testId: 'manage-gathering' })}
        {g.status === 'published' && toolPill('+ INVITE PEOPLE', () => setInviteOpen(true))}
        {g.isOwner && toolPill('+ CO-HOST', () => setCoHostOpen(true), { testId: 'add-cohost' })}
        {g.requiresApplication && toolPill('COMP SOMEONE', () => setCompOpen(true))}
        {g.requiresApplication && starter && toolPill('GREENLIGHT', greenlightGathering, { color: '#A3E635', border: '#A3E635' })}
        {isClub && clubId && toolPill('MANAGE CLUB', () => navigate(`/clubs/${clubId}/manage`))}
        {g.isOwner && toolPill('DELETE', deleteGathering, { color: '#F87171' })}
      </span>
    </div>
  );
  const allInBar = allIn && (
    <div className="flex flex-wrap items-center font-mono" style={{ gap: '8px 16px', padding: mobile ? '14px 16px' : '16px 20px', borderRadius: 20, background: '#000', color: '#fff' }} data-testid="allin-bar">
      <TagPill bg={GREEN_PILL} fg="#000" size={9} padding="4px 10px">YOU'RE IN</TagPill>
      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em' }}>{plural(heldCount, 'TICKET')} HELD · {plural(goingCount, 'RSVP')}</span>
      <button type="button" onClick={() => navigate('/my-tickets')} style={{ ...kicker(9), marginLeft: 'auto', color: SUBTLE, border: 'none', background: 'none', fontFamily: 'inherit', cursor: 'pointer', padding: 0 }}>ADDRESSES ARE IN MY TICKETS</button>
    </div>
  );
  const membersOnly = showMembersOnly && (
    <div className="flex flex-wrap items-center font-mono" style={{ gap: '12px 18px', padding: mobile ? '14px 16px' : '16px 20px', borderRadius: 20, background: CHIP }} data-testid="members-only">
      <span className="flex flex-col" style={{ gap: 4, flex: '1 1 280px' }}>
        <span style={kicker(11)}>MEMBERS ONLY</span>
        <span style={{ fontSize: 12, lineHeight: 1.55, color: BODY }}>RSVPs and tickets on this gathering are for members of {g.clubName || 'the club'}. You can look. Join the club to get in.</span>
      </span>
      {clubId && <PillButton size={44} onClick={() => navigate(`/clubs/${clubId}`)}>GO TO THE CLUB</PillButton>}
    </div>
  );

  // ── Entry panel ──
  const entryCta = () => {
    const btn = (label, onClick) => (
      <button type="button" onClick={onClick} disabled={entryBusy} className="font-mono" data-testid="entry-cta"
        style={{ height: 48, padding: '0 22px', border: '2px solid transparent', borderRadius: 999, background: starter ? RING_ON : '#000', color: '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', cursor: 'pointer', width: mobile ? '100%' : 'auto', opacity: entryBusy ? 0.6 : 1, boxSizing: 'border-box' }}>
        {entryBusy ? 'WORKING…' : label}
      </button>
    );
    const chip = (text, bg, fg = '#000') => <TagPill bg={bg} fg={fg} size={10} padding="8px 14px" data-testid="entry-chip">{text}</TagPill>;
    const again = SPENT_ENTRY.includes(entryStatus);
    if (!entryStatus || again) {
      const label = fee ? `${starter ? 'PLEDGE' : 'PAY'} ${money(g.entryFeeCents)}${starter ? '' : ' TO ATTEND'}` : g.mode === 'curated' ? 'APPLY TO ATTEND' : 'JOIN THE GATHERING';
      if (!again) return btn(label, startApply);
      // Say what happened, then offer the way back in. The chip alone is a dead end.
      return (
        <span className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <TagPill bg={CHIP} fg={MUTED} size={10} padding="8px 14px" data-testid="entry-chip">
            {entryStatus === 'refunded' ? 'REFUNDED' : entryStatus === 'declined' ? 'NOT ACCEPTED' : 'YOU WITHDREW'}
          </TagPill>
          {btn(entryStatus === 'declined' ? 'ASK AGAIN' : label, startApply)}
        </span>
      );
    }
    if (entryStatus === 'pending_payment') return btn(`FINISH PAYMENT — ${money(g.entryFeeCents)}`, () => setEntryOpen(true));
    if (entryStatus === 'accepted') return fee ? btn(`YOU'RE ACCEPTED — PAY ${money(g.entryFeeCents)}`, () => setEntryOpen(true)) : chip("YOU'RE IN", GREEN_PILL);
    if (entryStatus === 'paid') return chip(starter ? 'PLEDGED · IN ESCROW' : "YOU'RE IN", GREEN_PILL);
    const c = ENTRY_CHIP[entryStatus];
    return c ? chip(c.text, c.bg, c.fg) : null;
  };
  const entryPanel = showEntry && (
    <section className="flex flex-wrap items-center bg-white font-mono" style={{ gap: '12px 18px', padding: panelPad }} data-testid="entry-block">
      <span className="flex min-w-0 flex-col" style={{ gap: 6, flex: '1 1 280px' }}>
        <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <span style={kicker(11)}>{fee ? `ENTRY · ${money(g.entryFeeCents)}` : 'ENTRY · HOST APPROVES'}</span>
          {starter ? <TagPill bg={STARTER} fg="#fff" size={9}>PARTY STARTER</TagPill> : g.mode === 'curated' ? <TagPill bg={CURATED} size={9}>CURATED</TagPill> : <TagPill size={9}>ONE-TIME</TagPill>}
        </span>
        <span style={{ fontSize: 12, lineHeight: 1.55, color: BODY }}>
          {starter
            ? `It only happens if it reaches the goal${g.fundingDeadline ? ` by ${dayLabel(g.fundingDeadline)}` : ''}. If it doesn't, your ${money(g.entryFeeCents)} comes back.`
            : fee ? 'A one-time charge to attend the gathering. Separate from each event ticket.'
              : 'The host reads every application. You hear back before the weekend.'}
        </span>
        {starter && g.fundingGoalCents > 0 && (
          <span className="flex flex-col" style={{ gap: 6, maxWidth: 420, marginTop: 4 }} data-testid="pledge-bar">
            <span className="flex justify-between" style={{ fontSize: 11, fontWeight: 700 }}><span>PLEDGED</span><span>{money(g.pledgedCents || 0)} of {money(g.fundingGoalCents)}</span></span>
            <Bar pct={((g.pledgedCents || 0) / g.fundingGoalCents) * 100} track={STARTER_TRACK} fill={STARTER} />
          </span>
        )}
      </span>
      {entryCta()}
    </section>
  );

  // ── Agenda ──
  const rsvpGroup = (e) => (
    <span data-testid={`rsvp-${e.id}`}>
      <Segmented compact height={mobile ? 38 : 32} ariaLabel="RSVP" value={e.viewerRsvp} onChange={(k) => setRsvp(e.id, k || e.viewerRsvp)}
        options={[{ key: 'going', label: 'GOING', toggle: true }, { key: 'maybe', label: 'MAYBE', toggle: true }, { key: 'cant', label: "CAN'T", toggle: true }]} />
    </span>
  );
  const rowBtn = (label, onClick, grey = false) => (
    <PillButton variant={grey ? 'tertiary' : 'primary'} size={mobile ? 44 : 38} fontSize={10} onClick={onClick}>{label}</PillButton>
  );
  const rowAction = (e) => {
    if (g.isHost) {
      const f = hostFill(e);
      return (
        <span className="flex flex-col items-end" style={{ gap: 5, minWidth: 110 }} data-testid={`host-fill-${e.id}`}>
          <span style={kicker(10, '.12em')}>{f.label}</span>
          <Bar pct={f.pct} width={110} height={5} />
        </span>
      );
    }
    if (e.viewerHasTicket) return <TagPill bg={GREEN_PILL} fg="#000" size={10} padding="8px 14px" data-testid={`held-${e.id}`}>TICKET HELD</TagPill>;
    if (!canParticipate) return rowBtn('VIEW', () => navigate(`/events/${e.id}`), true);
    if (e.isExternalTicketing) return rowBtn('TICKETS ↗', () => navigate(`/events/${e.id}`));
    if (e.ticketPriceCents > 0) return rowBtn('GET IN', () => navigate(`/events/${e.id}`));
    return rsvpGroup(e);
  };
  const rowMeta = (e) => [[e.venueName, e.locationCity].filter(Boolean).join(', '), `${isTicketed(e) ? (e.ticketCount || e.goingCount || 0) : (e.goingCount || 0)} in`].filter(Boolean).join(' · ');

  const agendaBlock = (
    <div className="flex flex-col" style={{ gap: 10 }} data-testid="agenda">
      <span className="flex flex-wrap items-center justify-between" style={{ gap: 10, padding: mobile ? '16px 16px 0' : '4px 0 0' }}>
        <span className="flex flex-col" style={{ gap: 3 }}>
          <span style={kicker(12)}>THE AGENDA</span>
          <span style={{ ...kicker(10, '.14em'), color: SUBTLE }} data-testid="agenda-sub">{agendaSubLabel(allAgenda.length, totalDays, activeDay?.short)}</span>
        </span>
        <span role="tablist" aria-label="Agenda view" className="flex" style={{ gap: 4, padding: 4, borderRadius: 999, background: '#fff' }}>
          {[['full', 'FULL SCHEDULE'], ['mine', 'MY ITINERARY']].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)} className="font-mono"
              style={{ height: 32, padding: '0 14px', border: 'none', borderRadius: 999, background: view === k ? '#000' : 'transparent', color: view === k ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 9, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>{label}</button>
          ))}
        </span>
      </span>
      {agendaDays.length === 0 ? (
        <div className="flex flex-col bg-white font-mono" style={{ gap: 6, padding: mobile ? '22px 16px' : '28px 22px' }} data-testid="agenda-empty">
          <span style={kicker(12)}>{view === 'mine' ? 'NOTHING ON YOUR LIST YET.' : dayFilter ? 'NOTHING ON THIS DAY.' : 'NO EVENTS ON THE AGENDA YET.'}</span>
          <span style={{ fontSize: 12, color: MUTED }}>{view === 'mine' ? 'Switch to the full schedule and pick a few.' : dayFilter ? 'Tap the day again to see the whole agenda.' : 'The host has not attached any events.'}</span>
        </div>
      ) : agendaDays.map((d) => (
        <div key={d.key} className="flex flex-col" style={{ gap: 2 }} data-testid={`day-${d.key}`}>
          <span className="flex items-center justify-between bg-white" style={{ gap: 10, padding: mobile ? '12px 16px' : '14px 20px' }}>
            <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.08em' }}>{d.label}</span>
            <span style={{ ...kicker(9, '.14em'), color: SUBTLE }}>{plural(d.events.length, 'EVENT')}</span>
          </span>
          {d.events.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center bg-white" style={{ gap: '8px 18px', padding: mobile ? '14px 16px' : '16px 20px' }} data-testid={`row-${e.id}`}>
              <span style={{ flex: mobile ? '0 0 100%' : '0 0 150px', fontSize: 11, fontWeight: 700, letterSpacing: '.04em', color: MUTED }}>{timeRangeLabel(e.dateTime, e.endDateTime)}</span>
              <span className="flex min-w-0 flex-col" style={{ flex: '1 1 220px', gap: 4 }}>
                <button type="button" onClick={() => navigate(`/events/${e.id}`)} className="text-left hover:underline" style={{ fontSize: mobile ? 14 : 15, fontWeight: 700, lineHeight: 1.3, border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer', overflowWrap: 'anywhere' }}>{e.title}</button>
                <span style={{ fontSize: 11, color: MUTED }}>{rowMeta(e)}</span>
                {overlapMap[e.id] && <OverlapChip title={overlapMap[e.id][0].title} />}
              </span>
              <span className="flex items-center" style={{ gap: 14, marginLeft: 'auto' }}>
                <span style={{ fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap' }} data-testid={`price-${e.id}`}>{priceLabel(e)}</span>
                {rowAction(e)}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  // ── Rail cards ──
  const card = (children, testId, extra = {}) => (
    <section className="flex flex-col bg-white font-mono" style={{ gap: 14, padding: mobile ? '18px 16px' : 22, ...extra }} data-testid={testId}>{children}</section>
  );
  const head = (title, right, rightColor = SUBTLE) => (
    <span className="flex items-center justify-between" style={{ gap: 10 }}>
      <span style={kicker(11)}>{title}</span>
      {right && <span style={{ ...kicker(9, '.14em'), color: rightColor }}>{right}</span>}
    </span>
  );
  const kv = (k, v, { bold = true, size = 12, color } = {}) => (
    <span key={k} className="flex justify-between" style={{ gap: 12, fontSize: size }}>
      <span style={{ minWidth: 0, color: BODY }}>{k}</span>
      <span style={{ flexShrink: 0, fontWeight: bold ? 700 : 400, color }}>{v}</span>
    </span>
  );
  const bundleCard = showBundle && card(
    <>
      {head(labels.band, plural(bundleCount, 'TICKET'))}
      {!mobile && (
        <span className="flex items-end justify-between" style={{ gap: 12 }}>
          <span style={{ fontSize: 32, fontWeight: 800, lineHeight: 1 }}>{money(bundleSum)}</span>
          <span style={{ ...kicker(10, '.14em'), color: MUTED }}>IN TICKETS</span>
        </span>
      )}
      <div className="flex flex-col" style={{ gap: 7 }}>{bundleLines.map((l) => kv(l.title, money(l.cents)))}</div>
      {bundleTotal !== null && bundleTotal >= bundleSum && (
        <div className="flex flex-col" style={{ gap: 7, padding: '12px 14px', borderRadius: 18, background: '#F5F5F5' }}>
          <span className="flex justify-between" style={{ fontSize: 11 }}><span style={{ color: MUTED }}>FEES + GST + PROCESSING</span><span>{money(bundleTotal - bundleSum)}</span></span>
          <span className="flex justify-between" style={{ fontSize: 13, fontWeight: 800 }}><span>TOTAL ON CARD</span><span>{money(bundleTotal)}</span></span>
        </div>
      )}
      {!mobile && <RingCta onClick={openBuy} data-testid="take-all">{labels.cta}</RingCta>}
      <span style={{ fontSize: 11, lineHeight: 1.6, color: MUTED }}>One charge per event. Skip a night and the rest still stand. There is no bundle discount.</span>
      {notInCharge.length > 0 && (
        <div className="flex flex-col" style={{ gap: 6, paddingTop: 12, borderTop: RULE }} data-testid="not-in-charge">
          <span style={{ ...kicker(9), color: MUTED }}>NOT IN THE CHARGE</span>
          {notInCharge.map((e) => kv(e.title, e.isExternalTicketing ? 'OFF-PLATFORM' : 'FREE · RSVP', { bold: false, size: 11, color: MUTED }))}
        </div>
      )}
    </>,
    'bundle-card',
  );
  const yourWeekend = allIn && card(
    <>
      {head('YOUR WEEKEND', 'ALL SET', GREEN)}
      {allAgenda.filter((e) => e.viewerHasTicket).map((e) => kv(e.title, 'TICKET HELD', { color: GREEN }))}
      {allAgenda.filter((e) => !e.viewerHasTicket && e.viewerRsvp === 'going').map((e) => kv(e.title, 'GOING'))}
      <PillButton variant="cyan" size={50} onClick={() => navigate('/my-tickets')}>SHOW MY TICKETS</PillButton>
    </>,
    'your-weekend',
  );
  const numbersCard = g.isHost && card(
    <>
      {head('THE NUMBERS', 'LIVE')}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 2, background: CHIP }}>
        {[[String(whoCount), 'IN', GREEN], [manage ? String(manage.paidAllGuests?.length || 0) : '—', 'PAID FOR ALL', '#000'], [manage ? money(manage.ticketRevenueCents || 0) : '—', 'TICKETS', '#000']].map(([n, k, c]) => (
          <span key={k} className="flex flex-col items-center bg-white text-center" style={{ gap: 4, padding: '14px 4px' }}>
            <span style={{ fontSize: 17, fontWeight: 800, color: c, overflowWrap: 'anywhere' }}>{n}</span>
            <span style={{ ...kicker(8, '.14em'), color: MUTED }}>{k}</span>
          </span>
        ))}
      </div>
      <span style={{ fontSize: 11, lineHeight: 1.6, color: MUTED }}>Ticket money sits in escrow per event and releases 24h after each one's doors.</span>
    </>,
    'numbers-card',
  );
  const faceCap = mobile ? 7 : 9;
  const whoCard = card(
    <>
      {head(`WHO'S IN · ${whoCount}`)}
      <span className="flex flex-wrap" style={{ gap: 6 }}>
        {people.length === 0 && <span style={{ fontSize: 11, color: SUBTLE }}>Nobody yet.</span>}
        {people.slice(0, faceCap).map((m) => (
          <button key={m.userId} type="button" title={m.displayName} onClick={() => openProfile(m)} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', borderRadius: '50%' }}>
            <RoundFace src={m.avatarUrl} name={m.displayName || m.initials} size={38} />
          </button>
        ))}
        {people.length > faceCap && <span className="flex items-center justify-center rounded-full" style={{ width: 38, height: 38, background: CHIP, fontSize: 11, fontWeight: 800 }}>+{people.length - faceCap}</span>}
      </span>
      {!g.isHost && canParticipate && allAgenda.length > 0 && (
        <button type="button" onClick={goingToAll} disabled={bulkBusy || allGoing} className="font-mono" data-testid="going-to-all"
          style={{ height: 46, border: 'none', borderRadius: 999, background: allGoing ? CYAN_TINT : CHIP, color: '#000', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', cursor: allGoing ? 'default' : 'pointer', transition: 'background-color .2s' }}>
          {allGoing ? "YOU'RE GOING TO ALL OF IT" : bulkBusy ? 'UPDATING…' : "I'M GOING TO ALL OF IT"}
        </button>
      )}
    </>,
    'whos-in',
  );
  const railCards = <>{bundleCard}{yourWeekend}{numbersCard}{whoCard}</>;

  // ── Tab panels ──
  const panel = (title, right, children, testId) => (
    <section className="flex flex-col bg-white font-mono" style={{ gap: 14, padding: panelPad }} data-testid={testId}>
      {head(title, right)}
      {children}
    </section>
  );
  const linkBtn = (label, onClick, color = MUTED, aria) => (
    <button type="button" onClick={onClick} aria-label={aria} className="hover:underline" style={{ ...kicker(9, '.14em'), color, border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer' }}>{label}</button>
  );

  const announcementsPanel = panel('ANNOUNCEMENTS', announcements.length ? plural(announcements.length, 'POST') : 'FROM THE HOST', (
    <>
      {g.isHost && (
        <div className="flex flex-col" style={{ gap: 8, padding: 14, borderRadius: 18, background: '#F5F5F5' }}>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Write an announcement to everyone…" aria-label="Announcement"
            style={{ minHeight: 64, resize: 'vertical', padding: '4px 2px', border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.6, outline: 'none' }} />
          <span className="flex justify-between" style={{ gap: 8 }}>
            <button type="button" onClick={() => setPin((p) => !p)} aria-pressed={pin} className="font-mono" style={{ height: 34, padding: '0 14px', border: 'none', borderRadius: 999, background: pin ? '#000' : '#fff', color: pin ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 9, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer' }}>{pin ? 'PINNED TO TOP' : 'PIN TO TOP'}</button>
            <PillButton size={34} fontSize={10} onClick={postAnnouncement} disabled={!draft.trim()} style={{ opacity: draft.trim() ? 1 : 0.4 }}>POST</PillButton>
          </span>
        </div>
      )}
      {announcements.length === 0 ? <span style={{ fontSize: 12, color: MUTED }}>Nothing from the host yet.</span> : announcements.map((a) => (
        <div key={a.id} className="flex flex-col" style={{ gap: 8, padding: '14px 16px', borderRadius: 18, background: a.isPinned ? '#FFF8EA' : '#F5F5F5' }}>
          <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
            <RoundFace src={a.authorAvatarUrl} name={a.authorName || 'Host'} size={24} />
            <span style={{ fontSize: 11, fontWeight: 700 }}>{a.authorName || 'Host'}</span>
            {a.isPinned && <TagPill bg="#000" fg="#fff" size={8}>PINNED</TagPill>}
            <span style={{ ...kicker(9, '.12em'), marginLeft: 'auto', color: SUBTLE }}>{dateLabel(a.createdAt)}</span>
            {g.isHost && linkBtn('DELETE', () => deleteAnnouncement(a.id), '#DC2626')}
          </span>
          <p className="whitespace-pre-line" style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>{a.content}</p>
        </div>
      ))}
    </>
  ), 'announcements-panel');

  const taskRow = (task, isSub = false) => {
    const owned = !!task.ownerUserId;
    const mineClaim = !task.ownerIsAssigned && task.claimedByUserId === profile?.userId;
    return (
      <div key={task.id} className="flex flex-wrap items-center" style={{ gap: '8px 12px', padding: `12px 0 12px ${isSub ? 28 : 0}px`, borderTop: RULE }}>
        <span style={{ flexShrink: 0, width: 14, height: 14, borderRadius: '50%', border: '2px solid #000', background: owned ? '#000' : '#fff', boxSizing: 'border-box' }} aria-hidden="true" />
        <span className="min-w-0" style={{ flex: '1 1 200px', fontSize: 13 }}>{task.title}</span>
        {owned && (
          <span className="flex items-center" style={{ gap: 6, fontSize: 11, color: MUTED }}>
            <RoundFace src={task.ownerAvatarUrl} name={task.ownerName || '?'} size={20} />
            {task.ownerName}{task.ownerIsAssigned && <span style={{ color: SUBTLE }}> (assigned)</span>}
          </span>
        )}
        {canParticipate && !task.ownerIsAssigned && (!owned || mineClaim) && (
          <PillButton variant={mineClaim ? 'tertiary' : 'primary'} size={30} fontSize={9} onClick={() => toggleClaim(task)}>{mineClaim ? 'UNCLAIM' : 'CLAIM'}</PillButton>
        )}
        {g.isHost && (
          <span className="flex" style={{ gap: 10 }}>
            {task.ownerIsAssigned ? linkBtn('UNASSIGN', () => unassignTask(task.id)) : linkBtn('ASSIGN', () => setAssignForTask(task.id))}
            {linkBtn('DELETE', () => removeTask(task.id), '#DC2626', 'Delete task')}
          </span>
        )}
      </div>
    );
  };
  const tasksPanel = panel('TASKS', tasks.length ? plural(tasks.length, 'TASK') : "WHO'S DOING WHAT", (
    <>
      {g.isHost && (
        <span className="flex" style={{ gap: 6 }}>
          <input value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addTask(newTask); }} placeholder="Add a task…" aria-label="New task"
            style={{ flex: 1, minWidth: 0, height: 44, padding: '0 18px', border: 'none', borderRadius: 999, background: '#F5F5F5', fontFamily: 'inherit', fontSize: 13, outline: 'none' }} />
          <PillButton size={44} fontSize={10} onClick={() => addTask(newTask)}>ADD</PillButton>
        </span>
      )}
      {tasks.length === 0 ? <span style={{ fontSize: 12, color: MUTED }}>No tasks yet.</span> : (
        <div className="flex flex-col">
          {tasks.map((task) => (
            <div key={task.id}>
              {taskRow(task)}
              {(task.subtasks || []).map((st) => taskRow(st, true))}
              {g.isHost && (
                <div style={{ paddingLeft: 28, paddingBottom: 10 }}>
                  <input value={subtaskDrafts[task.id] || ''} onChange={(e) => setSubtaskDrafts((d) => ({ ...d, [task.id]: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') addTask(subtaskDrafts[task.id], task.id); }}
                    placeholder="+ subtask" aria-label={`Subtask for ${task.title}`} style={{ width: '100%', height: 34, padding: '0 14px', border: 'none', borderRadius: 999, background: '#F5F5F5', fontFamily: 'inherit', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  ), 'tasks-panel');

  const boardPanel = (
    <div data-testid="give-take"><GiveTakeBoard context="gathering" gatheringId={gatheringId} variant="surface" mobile={mobile} /></div>
  );

  const peoplePanel = (
    <section className="grid bg-white font-mono" style={{ gridTemplateColumns: mobile ? 'minmax(0,1fr)' : 'repeat(2,minmax(0,1fr))', gap: 18, padding: panelPad }} data-testid="people-wall">
      {[['HOSTS', g.hosts || []], ['MEMBERS', g.members || []]].map(([label, list]) => (
        <div key={label} className="flex flex-col" style={{ gap: 10 }}>
          <span style={kicker(11)}>{label} · {list.length}</span>
          <span className="flex flex-wrap" style={{ gap: 6 }}>
            {list.length === 0 && <span style={{ fontSize: 11, color: SUBTLE }}>Nobody yet.</span>}
            {list.map((m) => (
              <span key={m.userId} className="inline-flex items-center" style={{ gap: 4 }}>
                <FacePill src={m.avatarUrl} name={m.role === 'cohost' ? `${m.displayName || m.initials} · CO-HOST` : (m.displayName || m.initials)} onClick={() => openProfile(m)} />
                {m.role === 'cohost' && (g.isOwner || m.userId === profile?.userId) && (
                  <button type="button" onClick={() => removeCoHost(m)} aria-label={m.userId === profile?.userId ? 'Step down as co-host' : `Remove ${m.displayName} as co-host`} data-testid="remove-cohost"
                    style={{ width: 24, height: 24, border: 'none', borderRadius: 999, background: '#F2F2F2', color: '#000', fontSize: 12, lineHeight: 1, cursor: 'pointer' }}>×</button>
                )}
              </span>
            ))}
          </span>
        </div>
      ))}
    </section>
  );

  const guestsPanel = g.isHost && panel('GUESTS', `PAID FOR ALL · ${manage?.paidAllGuests?.length || 0}`, !manage ? <span style={{ fontSize: 12, color: MUTED }}>Loading…</span> : (
    <>
      {manage.paidAllGuests?.length > 0 && (
        <span className="flex flex-wrap" style={{ gap: 6 }}>
          {manage.paidAllGuests.map((m) => <FacePill key={m.userId} src={m.avatarUrl} name={m.displayName} size={24} bg={CYAN_TINT} onClick={() => openProfile(m)} />)}
        </span>
      )}
      <div className="flex flex-col">
        {(manage.events || []).map((ev) => {
          const open = expandedEvent === ev.eventId;
          return (
            <div key={ev.eventId} style={{ borderTop: RULE }}>
              <button type="button" onClick={() => setExpandedEvent(open ? null : ev.eventId)} aria-expanded={open} className="flex w-full items-center text-left" style={{ gap: 12, padding: '12px 0', border: 'none', background: 'transparent', fontFamily: 'inherit', cursor: 'pointer' }}>
                <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{ev.title}</span>
                  <span style={{ ...kicker(9, '.14em'), color: MUTED }}>{dayShort(ev.dateTime)} · {ev.isTicketed ? 'TICKETED' : 'FREE'} · {plural(ev.guestCount || 0, 'GUEST')}</span>
                </span>
                <span style={kicker(9, '.14em')}>{open ? 'HIDE' : 'SHOW'}</span>
              </button>
              {open && (
                <div className="flex flex-col" style={{ gap: 6, padding: '0 0 12px' }}>
                  {ev.guests.length === 0 ? <span style={{ fontSize: 12, color: MUTED }}>No guests yet.</span> : ev.guests.map((m) => (
                    <span key={m.userId} className="flex items-center" style={{ gap: 10, padding: '6px 12px 6px 6px', borderRadius: 999, background: '#F5F5F5' }}>
                      <RoundFace src={m.avatarUrl} name={m.displayName} size={26} />
                      <span className="min-w-0 flex-1 truncate" style={{ fontSize: 12, fontWeight: 700 }}>{m.displayName}{m.handle ? <span style={{ color: MUTED, fontWeight: 400 }}> @{m.handle}</span> : null}</span>
                      <TagPill bg={m.status === 'paid' ? '#000' : '#fff'} fg={m.status === 'paid' ? '#fff' : '#000'} size={8}>{m.status}{m.quantity > 1 ? ` ×${m.quantity}` : ''}</TagPill>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  ), 'guests-panel');

  const applicantChip = (a) => {
    const map = {
      paid: ['PAID', GREEN_PILL, '#000'], declined: ['NOT ACCEPTED', CHIP, MUTED], waitlisted: ['WAITLISTED', CHIP, MUTED],
      accepted: ['ACCEPTED', '#000', '#fff'], pending_payment: ['AWAITING PAYMENT', '#000', '#fff'],
    }[a.status] || [String(a.status || '').replace('_', ' ').toUpperCase(), CHIP, '#000'];
    return <TagPill bg={map[1]} fg={map[2]} size={9} padding="5px 11px">{map[0]}</TagPill>;
  };
  const hostEntryPanel = g.isHost && g.requiresApplication && panel('ENTRY', `${(g.applicants || []).length} APPLIED`, (
    <>
      <span className="flex flex-wrap" style={{ gap: 6 }}>
        <TagPill size={9} padding="5px 11px">{g.mode === 'curated' ? 'CURATED' : 'OPEN'}</TagPill>
        <TagPill size={9} padding="5px 11px">{fee ? money(g.entryFeeCents) : 'FREE'}</TagPill>
        {starter && <TagPill bg={STARTER} fg="#fff" size={9} padding="5px 11px">PARTY STARTER{g.fundingGoalCents > 0 ? ` · ${money(g.pledgedCents || 0)} OF ${money(g.fundingGoalCents)}` : ''}</TagPill>}
      </span>
      {starter && g.fundingGoalCents > 0 && <Bar pct={((g.pledgedCents || 0) / g.fundingGoalCents) * 100} track={STARTER_TRACK} fill={STARTER} style={{ maxWidth: 420 }} />}
      {(g.applicants || []).length === 0 ? <span style={{ fontSize: 12, color: MUTED }}>No applicants yet.</span> : g.applicants.map((a) => (
        <div key={a.userId} className="flex flex-wrap items-center" style={{ gap: '8px 12px', padding: '10px 0', borderTop: RULE }}>
          <RoundFace src={a.avatarUrl} name={a.displayName || a.initials} size={34} />
          <span className="min-w-0" style={{ flex: '1 1 140px', fontSize: 13 }}><span style={{ fontWeight: 700 }}>{a.displayName}</span>{a.handle ? <span style={{ color: MUTED }}> @{a.handle}</span> : null}</span>
          {a.status === 'pending' ? (
            <span className="flex" style={{ gap: 6 }}>
              <PillButton size={32} fontSize={9} onClick={() => reviewApplicant(a.userId, 'accept')}>ACCEPT</PillButton>
              <PillButton variant="tertiary" size={32} fontSize={9} onClick={() => reviewApplicant(a.userId, 'decline')}>DECLINE</PillButton>
            </span>
          ) : applicantChip(a)}
        </div>
      ))}
    </>
  ), 'entry-panel');

  const tabPanel = { entry: hostEntryPanel, guests: guestsPanel, ann: announcementsPanel, tasks: tasksPanel, board: boardPanel, people: peoplePanel }[activeTab];
  const tabsBlock = (
    <div className="flex flex-col" style={{ gap: 10 }} data-testid="gathering-tabs">
      <div role="tablist" className="flex" style={{ gap: 6, overflowX: 'auto', scrollbarWidth: 'none', padding: mobile ? '0 12px' : 0 }}>
        {tabs.map((t) => <RingTab key={t.key} role="tab" on={activeTab === t.key} height={mobile ? 36 : 40} fontSize={10} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>{t.label}</RingTab>)}
      </div>
      {tabPanel}
    </div>
  );

  // ── Phone sticky bar ──
  let bar = null;
  if (mobile) {
    if (g.isHost) bar = { price: `${whoCount} IN`, note: manage ? `${money(manage.ticketRevenueCents || 0)} IN TICKETS` : plural(allAgenda.length, 'EVENT'), cta: 'MANAGE', ctaBg: '#00FFFF', onCta: () => navigate(managePath) };
    else if (showMembersOnly) bar = { price: 'MEMBERS ONLY', note: (g.clubName || 'THE CLUB').toUpperCase(), cta: 'GO TO THE CLUB', onCta: () => clubId && navigate(`/clubs/${clubId}`) };
    else if (showBundle) bar = { price: money(bundleSum), note: `${plural(bundleCount, 'TICKET')} · FREE ONES ARE RSVPS`, cta: labels.short, onCta: openBuy };
    else if (allIn) bar = { price: "YOU'RE IN", note: `${plural(heldCount, 'TICKET')} · ${plural(goingCount, 'RSVP')}`, cta: 'MY TICKETS', ctaBg: '#00FFFF', onCta: () => navigate('/my-tickets') };
  }

  // ── Sheets ──
  const sheets = (
    <>
      <Sheet open={buyOpen} onClose={() => setBuyOpen(false)} phone={mobile} title={buyResult ? "YOU'RE IN" : labels.short}>
        {buyResult ? (
          <>
            <span className="flex items-center" style={{ gap: 10 }}>
              <TagPill bg={GREEN_PILL} fg="#000" size={9} padding="5px 11px">DONE</TagPill>
              <span style={{ fontSize: 12, color: BODY }}>Your tickets are in My Tickets and your wallet app.</span>
            </span>
            <div className="flex flex-col" style={{ gap: 2, background: CHIP }}>
              {buyResult.map((l) => (
                <span key={l.eventId} className="flex justify-between bg-white" style={{ gap: 12, padding: '12px 14px', fontSize: 12 }}>
                  <span className="min-w-0">{l.title}</span>
                  <span style={{ ...kicker(10, '.14em'), flexShrink: 0, color: l.status === 'failed' ? '#DC2626' : l.status === 'paid' || l.status === 'accepted' ? GREEN : '#000' }}>
                    {l.status === 'paid' || l.status === 'accepted' ? 'TICKET HELD' : l.status === 'going' ? 'GOING' : String(l.status || '').replace('_', ' ')}
                  </span>
                </span>
              ))}
            </div>
            <span className="flex" style={{ gap: 8 }}>
              <PillButton variant="tertiary" size={48} style={{ flex: 1 }} onClick={() => setBuyOpen(false)}>CLOSE</PillButton>
              <PillButton variant="cyan" size={48} style={{ flex: 1 }} onClick={() => navigate('/my-tickets')}>MY TICKETS</PillButton>
            </span>
          </>
        ) : (
          <GatheringCheckoutForm gatheringId={gatheringId} userEmail={profile?.email} onDone={onBought} />
        )}
      </Sheet>
      <Sheet open={entryOpen} onClose={() => setEntryOpen(false)} phone={mobile} title={starter ? 'PLEDGE TO ATTEND' : 'PAY TO ATTEND'}>
        <GatheringEntryForm gatheringId={gatheringId} userEmail={profile?.email} onDone={onEntryPaid} gatheringName={g.name} partyStarter={starter} fundingDeadline={g.fundingDeadline} />
      </Sheet>
      <AddPeopleModal title="Invite to gathering" ctaLabel="Invite" placeholder="Search people to invite…" isOpen={inviteOpen} onClose={() => setInviteOpen(false)} onAdd={invitePeople} excludeUserIds={[profile?.userId].filter(Boolean)} />
      <AddPeopleModal title="Add a co-host" ctaLabel="Make co-host" placeholder="Search people to co-host…" isOpen={coHostOpen} onClose={() => setCoHostOpen(false)} onAdd={addCoHosts} excludeUserIds={[profile?.userId, ...(g.hosts || []).map((h) => h.userId)].filter(Boolean)} />
      <AddPeopleModal title="Comp free entry" ctaLabel="Comp" placeholder="Search people to comp…" isOpen={compOpen} onClose={() => setCompOpen(false)} onAdd={compGuests} excludeUserIds={[profile?.userId].filter(Boolean)} />
      <AddPeopleModal title="Assign task" ctaLabel="Assign" placeholder="Search people to assign…" isOpen={!!assignForTask} onClose={() => setAssignForTask(null)} onAdd={assignTaskTo} />
    </>
  );

  const body = (
    <main ref={mainRef} className="flex w-full flex-col font-mono" style={{ maxWidth: 1280, margin: '0 auto', padding: mobile ? '0 0 16px' : '24px 32px 72px', gap, boxSizing: 'border-box' }} data-testid="gathering-page">
      {!mobile && (
        <span className="flex" style={{ ...kicker(10), gap: 8, color: SUBTLE, whiteSpace: 'nowrap', overflow: 'hidden' }} data-testid="breadcrumb">
          <button type="button" onClick={backTo} style={{ color: '#000', border: 'none', background: 'none', padding: 0, font: 'inherit', letterSpacing: 'inherit', cursor: 'pointer' }}>← {backLabel}</button>
          <span>/</span><span>GATHERINGS</span><span>/</span><span className="truncate">{g.name}</span>
        </span>
      )}
      {cover}
      {coverEditor}
      {dayStrip}
      {hostBar}
      {allInBar}
      {membersOnly}
      {entryPanel}
      <div className="grid items-start" style={{ gridTemplateColumns: wide ? 'minmax(0,1fr) 380px' : 'minmax(0,1fr)', gap }}>
        <div className="flex min-w-0 flex-col" style={{ gap }}>
          {agendaBlock}
          {!wide && <div className="flex flex-col" style={{ gap }} data-testid="inline-rail">{railCards}</div>}
          {tabsBlock}
        </div>
        {wide && <StickyRail width={380} scroll>{railCards}</StickyRail>}
      </div>
    </main>
  );

  if (mobile) {
    return (
      <SoftSurface>
        <SurfacePage mobileTitle={crumb(g.name)} mobileEyebrow={mobileEyebrow} onBack={backTo}>
          {body}
          {bar && <StickyCtaBar price={bar.price} note={bar.note} cta={bar.cta} onCta={bar.onCta} ctaBg={bar.ctaBg} />}
          {sheets}
        </SurfacePage>
      </SoftSurface>
    );
  }
  return (
    <SoftSurface>
      <SurfacePage bare>
        {body}
        {sheets}
      </SurfacePage>
    </SoftSurface>
  );
}
